import { formatNameList, timeOfDay, weekdayName } from './generateClusterSummary';
import type { TemplateKind } from './eventTemplates';

export interface PhotoCaptionInput {
  templateKind: TemplateKind | null;
  takenAt: Date | null;
  location: string | null;
  participantNames: string[];
}

type Branches = {
  both: (names: string, location: string) => string;
  namesOnly: (names: string) => string;
  locationOnly: (location: string) => string;
  neither: (whenText: string) => string;
};

const TEMPLATE_BRANCHES: Record<Exclude<TemplateKind, 'general'>, Branches> = {
  birthday: {
    both: (names, location) => `Celebrating with ${names} in ${location}`,
    namesOnly: names => `Celebrating with ${names}`,
    locationOnly: location => `Birthday celebration in ${location}`,
    neither: whenText => `Birthday celebration — ${whenText}`,
  },
  trip: {
    both: (names, location) => `Exploring ${location} with ${names}`,
    namesOnly: names => `With ${names} on the trip`,
    locationOnly: location => `Exploring ${location}`,
    neither: whenText => `${whenText} on the trip`,
  },
  graduation: {
    both: (names, location) => `Celebrating the graduation with ${names} in ${location}`,
    namesOnly: names => `Celebrating the graduation with ${names}`,
    locationOnly: location => `Graduation day in ${location}`,
    neither: whenText => `Graduation day — ${whenText}`,
  },
  wedding: {
    both: (names, location) => `With ${names} on the wedding day in ${location}`,
    namesOnly: names => `With ${names} on the wedding day`,
    locationOnly: location => `The wedding day in ${location}`,
    neither: whenText => `The wedding day — ${whenText}`,
  },
  memorial: {
    both: (names, location) => `Remembering, with ${names} in ${location}`,
    namesOnly: names => `Remembering, with ${names}`,
    locationOnly: location => `A moment to remember, in ${location}`,
    neither: whenText => `A moment to remember — ${whenText}`,
  },
  reunion: {
    both: (names, location) => `Reunion with ${names} in ${location}`,
    namesOnly: names => `Reunion with ${names}`,
    locationOnly: location => `Family reunion in ${location}`,
    neither: whenText => `Family reunion — ${whenText}`,
  },
  holiday: {
    both: (names, location) => `Holiday time with ${names} in ${location}`,
    namesOnly: names => `Holiday time with ${names}`,
    locationOnly: location => `Holiday moment in ${location}`,
    neither: whenText => `Holiday, ${whenText}`,
  },
};

/**
 * Heuristic per-photo caption — no LLM, extends generateClusterSummary.ts's
 * pattern (EXIF date/time, reverse-geocoded location, face-matched names) with
 * a per-template opener. `general`/null falls back to generateTitle's exact
 * existing phrasing so the "no template chosen" case reads identically to
 * what the app already produces today. No signal at all -> '' (never shown
 * as a suggestion, never a forced guess).
 */
export function generatePhotoCaption(input: PhotoCaptionInput): string {
  const names = formatNameList(input.participantNames);
  const location = input.location ?? '';
  const whenText = input.takenAt ? `${weekdayName(input.takenAt)} ${timeOfDay(input.takenAt)}` : '';

  if (!input.templateKind || input.templateKind === 'general') {
    if (location && names) return `${location} with ${names}`;
    if (location) return location;
    if (names) return `With ${names}`;
    if (whenText) return whenText;
    return '';
  }

  const branches = TEMPLATE_BRANCHES[input.templateKind];
  if (location && names) return branches.both(names, location);
  if (names) return branches.namesOnly(names);
  if (location) return branches.locationOnly(location);
  if (whenText) return branches.neither(whenText);
  return '';
}
